import { useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useAuth } from "@bw/shared/hooks";
import { getAuthErrorMessage } from "@bw/shared/auth-errors";
import { AuthCard } from "../components/AuthCard";

const RESEND_FALLBACK =
  "Could not re-send the verification email. Please try again in a moment.";

/** Post-registration email verification and resend recovery surface. */
export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const email = (searchParams.get("email") ?? "").trim();
  const initialDeliveryFailed = searchParams.get("delivery") === "failed";
  const accountNameIncomplete = searchParams.get("name") === "incomplete";
  const {
    resendVerificationEmail,
    canResendVerificationEmail = false,
    loading,
  } = useAuth();

  const returnUrl = searchParams.get("returnUrl");
  const signInHref = returnUrl
    ? `/login?returnUrl=${encodeURIComponent(returnUrl)}`
    : "/login";

  const [status, setStatus] = useState<
    "idle" | "initial_error" | "sending" | "sent" | "verified" | "error"
  >(initialDeliveryFailed ? "initial_error" : "idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleResend = async () => {
    if (status === "sending") return;

    setStatus("sending");
    setErrorMessage(null);
    try {
      const result = await resendVerificationEmail?.(returnUrl ?? undefined);
      setStatus(result === "already_verified" ? "verified" : "sent");
    } catch (error) {
      setErrorMessage(getAuthErrorMessage(error, RESEND_FALLBACK));
      setStatus("error");
    }
  };

  const strongEmail = email ? (
    <Box component="strong" sx={{ color: "text.primary" }}>
      {email}
    </Box>
  ) : null;

  return (
    <AuthCard
      heading="Verify your email"
      tagline="One quick step before you start playing"
    >
      <Paper variant="outlined" sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Stack spacing={2.25}>
          {accountNameIncomplete ? (
            <Alert severity="warning">
              Your account was created, but its name could not be saved. You can
              finish your account name in Edit profile after signing in.
            </Alert>
          ) : null}
          <Typography color="text.secondary" variant="body2">
            {loading && !canResendVerificationEmail ? (
              <>Checking your verification options…</>
            ) : status === "initial_error" ? (
              email ? (
                <>
                  Your account was created for {strongEmail}, but the first
                  verification email was not sent.{" "}
                  {canResendVerificationEmail
                    ? "Use Resend below to try again."
                    : "Sign in below to request a new one."}
                </>
              ) : (
                <>
                  Your account was created, but the first verification email was
                  not sent.{" "}
                  {canResendVerificationEmail
                    ? "Use Resend below to try again."
                    : "Sign in below to request a new one."}
                </>
              )
            ) : !canResendVerificationEmail ? (
              email ? (
                <>
                  Sign in below to request a verification link for {strongEmail}
                  .
                </>
              ) : (
                <>
                  Sign in below to request a verification link for your account.
                </>
              )
            ) : email ? (
              <>
                We sent a verification link to {strongEmail}. Open it to confirm
                your address, then sign in.
              </>
            ) : (
              <>
                Email verification is required. Send a verification link below
                to continue.
              </>
            )}
          </Typography>

          {status === "initial_error" ? (
            <Alert severity="error" role="alert">
              The first verification email was not sent.{" "}
              {canResendVerificationEmail
                ? "Resend it to continue."
                : "Sign in to request a new one."}
            </Alert>
          ) : null}

          {status === "sent" ? (
            <Alert severity="success" role="status">
              Verification email re-sent — check your inbox (and spam).
            </Alert>
          ) : null}

          {status === "verified" ? (
            <Alert severity="success" role="status">
              Your email is already verified. You can sign in.
            </Alert>
          ) : null}

          {status === "error" && errorMessage ? (
            <Alert severity="error" role="alert">
              {errorMessage}
            </Alert>
          ) : null}

          {canResendVerificationEmail ? (
            <Button
              type="button"
              variant="contained"
              size="small"
              fullWidth
              onClick={handleResend}
              disabled={status === "sending" || status === "verified"}
              startIcon={
                status === "sending" ? (
                  <CircularProgress
                    size={16}
                    color="inherit"
                    aria-hidden="true"
                  />
                ) : undefined
              }
            >
              {status === "sending"
                ? "Sending…"
                : status === "verified"
                  ? "Email already verified"
                  : "Resend verification email"}
            </Button>
          ) : !loading ? (
            <Button
              component={RouterLink}
              to={signInHref}
              variant="contained"
              size="small"
              fullWidth
            >
              Sign in to request a verification email
            </Button>
          ) : null}

          {canResendVerificationEmail ? (
            <Link component={RouterLink} to={signInHref}>
              Back to sign in
            </Link>
          ) : null}
        </Stack>
      </Paper>
    </AuthCard>
  );
}
